import { Box } from "@mantine/core";

export type DecorativeCircleSpec = {
  size: string;
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
  color: string;
};

/** Fill color for decorative circles on dark brand surfaces. */
export const darkSurfaceCircleColor = "rgba(255, 255, 255, 0.055)" as const;

/** Default two-circle arrangement used on dark brand surfaces. */
export const darkSurfaceCircles: DecorativeCircleSpec[] = [
  { size: "10rem", top: "-4rem", right: "-2rem", color: darkSurfaceCircleColor },
  { size: "11rem", right: "3rem", bottom: "-6rem", color: darkSurfaceCircleColor },
];

/** Accent-tone single circle used on light accent surfaces. */
export const accentSurfaceCircles: DecorativeCircleSpec[] = [
  { size: "11rem", right: "-2rem", bottom: "-4rem", color: "var(--mantine-color-accent-1)" },
];

function circleRadius(size: string): string {
  return `calc(${size} / 2)`;
}

function circleCenterPosition(spec: DecorativeCircleSpec): { x: string; y: string } {
  const halfSize = circleRadius(spec.size);

  if (spec.right !== undefined && spec.top !== undefined) {
    return {
      x: `calc(100% - (${spec.right} + ${halfSize}))`,
      y: `calc(${spec.top} + ${halfSize})`,
    };
  }

  if (spec.right !== undefined && spec.bottom !== undefined) {
    return {
      x: `calc(100% - (${spec.right} + ${halfSize}))`,
      y: `calc(100% - (${spec.bottom} + ${halfSize}))`,
    };
  }

  if (spec.left !== undefined && spec.top !== undefined) {
    return {
      x: `calc(${spec.left} + ${halfSize})`,
      y: `calc(${spec.top} + ${halfSize})`,
    };
  }

  if (spec.left !== undefined && spec.bottom !== undefined) {
    return {
      x: `calc(${spec.left} + ${halfSize})`,
      y: `calc(100% - (${spec.bottom} + ${halfSize}))`,
    };
  }

  return { x: "50%", y: "50%" };
}

function circleSpecToGradientLayer(spec: DecorativeCircleSpec): string {
  const radius = circleRadius(spec.size);
  const { x, y } = circleCenterPosition(spec);
  return `radial-gradient(circle ${radius} at ${x} ${y}, ${spec.color} 0 99.5%, transparent 100%)`;
}

/** CSS background-image equivalent (radial-gradient layers) for surfaces styled via CSS vars. */
export function decorativeCirclesToBackgroundImage(circles: DecorativeCircleSpec[]): string {
  return circles.map(circleSpecToGradientLayer).join(", ");
}

/** Absolutely positioned aria-hidden circles; parent needs position: relative + overflow: hidden. */
export function DecorativeCircles({ circles }: { circles: DecorativeCircleSpec[] }) {
  return (
    <>
      {circles.map((circle, index) => (
        <Box
          key={`${circle.size}-${circle.top ?? ""}-${circle.right ?? ""}-${circle.bottom ?? ""}-${circle.left ?? ""}-${index}`}
          aria-hidden
          pos="absolute"
          w={circle.size}
          h={circle.size}
          top={circle.top}
          right={circle.right}
          bottom={circle.bottom}
          left={circle.left}
          bg={circle.color}
          style={{ borderRadius: "50%", pointerEvents: "none" }}
        />
      ))}
    </>
  );
}
